import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { COUNTRIES } from '../data/countries';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';

// Feb 25 2026 · Preview compile fix.
// A regex literal nested inside a template literal inside a JSX
// attribute (`data-testid={`country-option-${c.replace(/\s+/g,'-')}`}`)
// interacts badly with `babel-plugin-transform-remove-console` when
// Emergent Preview injects it alongside the Visual Edits Babel plugin,
// crashing with "Cannot read properties of null (reading 'reverse')".
// Hoisting the regex + slugify helper to module scope flattens the AST
// so the plugin doesn't touch nested regex/template-literal paths.
// No functional change — same slug output, same test IDs, same UX.
const SPACE_RE = /\s+/g;
const slugifyTestId = (s) => s.replace(SPACE_RE, '-');

/**
 * CountrySelect — searchable country dropdown (JF-04).
 * Stores the full standardised country name (e.g. "Tunisia", "Germany").
 * No "Other / Not listed" fallback — the list covers all UN member states.
 */
export const CountrySelect = ({ value, onChange, placeholder = 'Select country…', id, testId, invalid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return COUNTRIES;
    return COUNTRIES.filter((c) => c.toLowerCase().includes(needle));
  }, [q]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId || 'country-select-trigger'}
          className={`w-full justify-between font-normal ${invalid ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
        >
          <span className={value ? '' : 'text-muted-foreground'}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            autoFocus
            placeholder="Search country…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9"
            data-testid="country-select-search"
          />
        </div>
        <div className="max-h-60 overflow-y-auto" data-testid="country-select-list">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground text-center">No country found.</p>
          ) : (
            filtered.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setOpen(false); setQ(''); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/40 text-left"
                data-testid={`country-option-${slugifyTestId(c)}`}
              >
                <Check className={`h-4 w-4 ${value === c ? 'opacity-100' : 'opacity-0'}`} />
                {c}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CountrySelect;
